import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate, Link } from 'react-router-dom';
import { Truck, UserPlus, Building2, ChevronRight } from 'lucide-react';

export default function Signup() {
  const { signupCompany, signupDriver } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState('choose');
  const [mode, setMode] = useState(null);
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChoose = (chosen) => { setMode(chosen); setStep('form'); };

  const handleSubmit = async () => {
    setError('');
    if (!name || !email || !password || !confirm) { setError('Please fill in all fields.'); return; }
    if (mode === 'company' && !companyName) { setError('Please enter your company name.'); return; }
    if (mode === 'driver' && !companyCode) { setError('Please enter your company code from your admin.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      if (mode === 'company') {
        await signupCompany(email, password, name, companyName);
      } else {
        await signupDriver(email, password, name, companyCode.trim());
      }
      navigate('/');
    } catch (e) {
      if (e.message === 'invalid-company') {
        setError('Company code not found. Ask your fleet manager for the correct code.');
      } else if (e.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else {
        setError('Could not create account. Please try again.');
      }
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'var(--bg)' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Truck size={40} color="var(--accent)" style={{ marginBottom: 12 }} />
          <h1 style={{ fontSize: 28, marginBottom: 4 }}>Create Account</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Daily Driver Pulse</p>
        </div>

        {step === 'choose' && (
          <div>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 15, marginBottom: 20 }}>Which best describes you?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button type="button" onClick={() => handleChoose('driver')} style={{ display: 'flex', alignItems: 'center', gap: 16, background: 'var(--bg2)', border: '2px solid var(--border)', borderRadius: 12, padding: '20px', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 48, height: 48, background: 'var(--accent-dim)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Truck size={24} color="var(--accent)" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 18, color: 'var(--text)', marginBottom: 3 }}>I'm a Driver</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.4 }}>My company already uses Daily Driver Pulse and gave me a company code</div>
                </div>
                <ChevronRight size={18} color="var(--text-dim)" />
              </button>

              <button type="button" onClick={() => handleChoose('company')} style={{ display: 'flex', alignItems: 'center', gap: 16, background: 'var(--bg2)', border: '2px solid var(--border)', borderRadius: 12, padding: '20px', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 48, height: 48, background: 'var(--accent-dim)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Building2 size={24} color="var(--accent)" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 18, color: 'var(--text)', marginBottom: 3 }}>I'm a Fleet Owner / Manager</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.4 }}>I want to set up Daily Driver Pulse for my company and drivers</div>
                </div>
                <ChevronRight size={18} color="var(--text-dim)" />
              </button>
            </div>
            <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: 'var(--text-muted)' }}>
              Already have an account?{' '}<Link to="/login" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Sign In</Link>
            </p>
          </div>
        )}

        {step === 'form' && (
          <div>
            <button type="button" onClick={() => setStep('choose')} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>← Back</button>
            <div className="card">
              {mode === 'company' && (
                <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  <Building2 size={13} style={{ marginRight: 6, verticalAlign: 'middle', color: 'var(--accent)' }} />
                  You'll get an <strong style={{ color: 'var(--text)' }}>Admin account</strong> with a unique Company Code. Share that code with your drivers so they can sign up and link to your company.
                </div>
              )}
              {mode === 'driver' && (
                <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  <Truck size={13} style={{ marginRight: 6, verticalAlign: 'middle', color: 'var(--accent)' }} />
                  You need the <strong style={{ color: 'var(--text)' }}>Company Code</strong> from your fleet manager or owner. Ask them for it before signing up.
                </div>
              )}
              <div className="field"><label>Your Full Name</label><input value={name} onChange={e => setName(e.target.value)} placeholder="John Smith" autoFocus /></div>
              {mode === 'company' && <div className="field"><label>Company Name</label><input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Trucking LLC" /></div>}
              {mode === 'driver' && <div className="field"><label>Company Code</label><input value={companyCode} onChange={e => setCompanyCode(e.target.value)} placeholder="Paste the code from your fleet manager" /></div>}
              <div className="field"><label>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" /></div>
              <div className="field"><label>Password</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" /></div>
              <div className="field"><label>Confirm Password</label><input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" onKeyDown={e => e.key === 'Enter' && handleSubmit()} /></div>
              {error && <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</p>}
              <button type="button" className="btn-primary" onClick={handleSubmit} disabled={loading} style={{ width: '100%', padding: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <UserPlus size={15} /> {loading ? 'Creating account...' : 'Create Account'}
              </button>
              <p style={{ textAlign: 'center', marginTop: 16, fontSize: 14, color: 'var(--text-muted)' }}>
                Already have an account?{' '}<Link to="/login" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Sign In</Link>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
